import { BindAll } from 'lodash-decorators'
import {
  EnumBuyWay,
  EnumContentType,
  EnumContentTypeText,
  EnumGiftStatus,
  EnumGroupStatus,
  EnumInvoiceState,
  EnumOrderButtonType,
  EnumOrderState,
  EnumWaterfallLoadResult
} from '@xt/client/enums'
import { AjaxBasics, EnumApiOrder } from '@xt/client'
import { Course } from '@xt/client/entities/basics/course'
import { action, computed, observable } from 'mobx'
import { $service } from '@xt/client/experimental/request/service'

import type { UserInfo } from '@xt/client/entities/user/types'
import type { OrderListItemResponse, OrderItem, CheckRefundResult } from './types.d'
import moment from 'moment'

export type Option = {
  label: string
  value: string
}

@BindAll()
export class MyAccountOrderController {
  options: Option[] = [
    {
      label: '全部',
      value: ''
    },
    {
      label: '待成团',
      value: '4'
    },
    {
      label: '退款中',
      value: '2'
    },
    {
      label: '已退款',
      value: '3'
    }
  ]

  @observable option: string = ''

  @observable key: number = 0

  @computed get identifier() {
    return `${this.option}-${this.key}`
  }

  private isLoading: boolean = false

  private page: number = 0

  private pageSize: number = 20

  @observable list: Array<OrderItem> = []

  @action
  onChangeOption(option: string) {
    if (option === this.option) return

    this.option = option
    this.page = 0
    this.list = []
    this.isLoading = false
  }

  @action
  onAppendOrderList(list: Array<OrderItem>) {
    this.list = [...this.list, ...list]
  }

  public getServerTime() {
    return AjaxBasics.serviceDate
  }

  /**
   * 将EnumContentType映射为文本
   * @param contentType EnumContentType
   * @returns
   */
  private mapOrderTag(contentType: EnumContentType) {
    switch (contentType) {
      case EnumContentType.Painting:
        return EnumContentTypeText.Painting
      case EnumContentType.Calligraphy:
        return EnumContentTypeText.Calligraphy
      case EnumContentType.CalligraphyAndReading:
        return EnumContentTypeText.CalligraphyAndReading
      case EnumContentType.Health:
        return EnumContentTypeText.Health
      case EnumContentType.Physical:
        return EnumContentTypeText.Physical
      case EnumContentType.Reading:
        return EnumContentTypeText.Reading
      case EnumContentType.Writing:
        return EnumContentTypeText.Writing
      case EnumContentType.AestheticEducation:
        return EnumContentTypeText.AestheticEducation
      default:
        return EnumContentTypeText.Calligraphy
    }
  }

  /**
   * 订单状态（0未支付，1已支付，2退款中，3已退款，4已取消）
   * @param orderStatus
   * @returns
   */
  private mapOrderStatus(orderStatus: number) {
    switch (orderStatus) {
      case 0:
        return EnumOrderState.NotPay
      case 1:
        return EnumOrderState.HasPay
      case 2:
        return EnumOrderState.Refund
      case 3:
        return EnumOrderState.HasRefund
      case 4:
        return EnumOrderState.Cancelled
      case 5:
        return EnumOrderState.ToBeConfirmed
      default:
        return EnumOrderState.Cancelled
    }
  }

  /**
   * 如果是赠课订单 额外格式化赠课信息
   */
  private parseGiftOrderInfo(data: OrderListItemResponse, currentUser: UserInfo): OrderItem['gift'] | undefined {
    if (data.buyWay === EnumBuyWay.Give) {
      return {
        isGiver: currentUser.id === data.memberId,
        showGiveFail: currentUser.id === data.memberId && [EnumGiftStatus.Fail, EnumGiftStatus.Wait].includes(data.receiverStatus),
        showGiveSuccess: currentUser.id === data.memberId && data.receiverStatus === EnumGiftStatus.Success,
        // 手机号优先级比邮箱账号优先级高
        type: data.receiverPhoneNum ? 'phone' : 'email',
        account: data.receiverPhoneNum || data.receiverEmail,
        recipient: {
          nickname: data.receiverNickname,
          memberId: data.receiverId
        },
        giver: {
          nickname: data.memberNickname,
          memberId: data.memberId
        }
      }
    }

    return undefined
  }

  private parseGroupOrderInfo(data: OrderListItemResponse, currentUser: UserInfo): OrderItem['group'] | undefined {
    /**
     * 先判断是否开启了拉手
     *
     * 最后一位是 "1" 则开启了团购活动
     */
    if (data.activityType === 0) return undefined

    if (!data.groupNo) return undefined

    const isCancelled = this.mapOrderStatus(data.orderStatus) === EnumOrderState.Cancelled

    /**
     * 拼团的创建时间加24个小时计算结束时间
     */
    const groundEndTime = moment(data.groupCreateTime).add(24, 'hour')
    const realGroupEndTime = groundEndTime.isAfter(data.signUpEndTime) ? data.signUpEndTime : groundEndTime.valueOf()

    return {
      id: data.groupNo,
      createTime: data.groupCreateTime,
      isOwner: currentUser.id === data.firstGroupMemberId,
      firstGroupMemberId: data.firstGroupMemberId || currentUser.id,
      otherGroupMemberId: data.otherGroupMemberId,
      showGroupFail: data.status === EnumGroupStatus.Fail || isCancelled,
      showGroupSuccess: data.status === EnumGroupStatus.Success && !isCancelled,
      showInterval: data.status === 0 && currentUser.id === data.firstGroupMemberId && !isCancelled,
      // 因为报名结束后仍有团处于等待成团状态 这种情况下拼团倒计时根据报名结束时间来计算
      groupEndTime: realGroupEndTime
    }
  }

  private mapInvoiceState(invoiceStatus: number | null): EnumInvoiceState {
    switch (invoiceStatus) {
      case null:
        return EnumInvoiceState.NotApply
      case 0:
        return EnumInvoiceState.WaitInvoice
      case 1:
        return EnumInvoiceState.HasInvoice
      case 2:
        return EnumInvoiceState.Reject
      case 3:
        return EnumInvoiceState.Cancellation
      default:
        return EnumInvoiceState.NotApply
    }
  }

  private parseOrderItem(item: OrderListItemResponse, currentUser: UserInfo): OrderItem {
    const endTime = moment(item.createTime).add(30, 'minutes')

    return {
      orderNo: item.orderNo,
      tag: this.mapOrderTag(item.contentType),
      orderStatus: this.mapOrderStatus(item.orderStatus),
      gift: this.parseGiftOrderInfo(item, currentUser),
      group: this.parseGroupOrderInfo(item, currentUser),
      createTime: moment(item.createTime).format('YYYY.MM.DD HH:mm:ss'),
      cover: `${item.coverUrl}?x-oss-process=image/resize,w_${160},h_${100},m_fill/quality,q_90`,
      productName: item.productName,
      payAmount: item.payAmount / 100,
      refundEndTime: item.refundEndTime ? moment(item.refundEndTime).format('YYYY.MM.DD HH:mm:ss') : '',
      isRefundEnd: !!item.refundEndTime && this.getServerTime().isAfter(item.refundEndTime),
      payUnit: '元',
      orderEndTime: endTime.valueOf(),
      showOrderCountdown: this.mapOrderStatus(item.orderStatus) === EnumOrderState.NotPay && this.getServerTime().isBefore(endTime),
      invoiceState: this.mapInvoiceState(item.invoiceStatus),
      raw: item
    }
  }

  /**
   * 映射应该展示的订单按钮
   */
  public mapOrderButtons(data: OrderItem): Array<EnumOrderButtonType> {
    /**
     * 0元订单
     */
    const isZeroOrder = data.raw.payAmount <= 0
    /**
     * 自买 或者 当前是赠课人
     */
    const isSelfBuy = data.gift === undefined || data.gift.isGiver
    /**
     * 没有开启拼团 或者 开启拼团并且拼团已经成功
     */
    const isGroupSuccess = data.raw.activityType === 0 || data.raw.status === EnumGroupStatus.Success
    /**
     * 是否是随报随学
     */
    const isSignupAndLearn = data.raw.signUpEndTime === 2147000000000

    const btns: Array<EnumOrderButtonType> = []

    // 退课
    if (
      !isSignupAndLearn &&
      [EnumOrderState.HasPay, EnumOrderState.Refund, EnumOrderState.ToBeConfirmed].includes(data.orderStatus as EnumOrderState) &&
      (!data.gift || !data.gift.isGiver) &&
      (!data.group || data.raw.status !== EnumGroupStatus.Fail)
    ) {
      btns.push(EnumOrderButtonType.RefundEndNotice)
    }

    // 待支付
    if (data.orderStatus === EnumOrderState.NotPay) {
      btns.push(EnumOrderButtonType.CancelOrder, EnumOrderButtonType.ToPay)
    }

    // 发票相关
    if (!isZeroOrder && isSelfBuy && data.orderStatus === EnumOrderState.HasPay) {
      if ([EnumInvoiceState.NotApply, EnumInvoiceState.Reject].includes(data.invoiceState)) {
        // 申请发票
        if (isGroupSuccess) {
          btns.push(EnumOrderButtonType.InvoiceApply)
        }
      } else if (data.invoiceState === EnumInvoiceState.WaitInvoice) {
        // 发票开具中
        btns.push(EnumOrderButtonType.WaitInvoice)
      } else if (data.invoiceState === EnumInvoiceState.HasInvoice) {
        // 发票已开具
        btns.push(EnumOrderButtonType.InvoiceSucess)
      }
    }

    // 申请退课
    if ((!data.gift || !data.gift.isGiver) && data.raw.orderAmount !== 0 && data.orderStatus === EnumOrderState.HasPay) {
      if (data.raw.refund && data.raw.refundEndTime && this.getServerTime().isBefore(data.raw.refundEndTime)) {
        btns.push(EnumOrderButtonType.RefundApply)
      }
    }

    // 取消退课
    if ((!data.gift || !data.gift.isGiver) && [EnumOrderState.Refund, EnumOrderState.ToBeConfirmed].includes(data.orderStatus as EnumOrderState)) {
      btns.push(EnumOrderButtonType.RefundCancel)
    }

    // 退课成功
    if (data.orderStatus === EnumOrderState.HasRefund) {
      if (data.gift && !data.gift.isGiver) {
        btns.push(EnumOrderButtonType.GiftRefundSuccess)
      } else {
        btns.push(EnumOrderButtonType.RefundSuccess)
      }
    }

    // 再次购买
    if ([EnumOrderState.HasRefund, EnumOrderState.Cancelled].includes(data.orderStatus as EnumOrderState)) {
      const signUpEndTimeValid = data.raw.signUpEndTime !== undefined && this.getServerTime().isBefore(data.raw.signUpEndTime)
      const turnPkgEndTimeValid = data.raw.turnPkgEndTime !== undefined && this.getServerTime().isBefore(data.raw.turnPkgEndTime)
      const timeValid = signUpEndTimeValid || turnPkgEndTimeValid

      const isHasBuy = Course.UserHasbuyGroup.some(item => item.productId === data.raw.productId || item.productId === data.raw.pkgId)

      const canSignUp = timeValid && !isHasBuy && data.raw.canSignUp === true && data.raw.publishStatus === 2

      if (canSignUp) {
        btns.push(EnumOrderButtonType.ReSignup)
      }
    }

    return btns
  }

  public mapCountdown(endTime: number): { isEnd: boolean; hours: string; minutes: string; seconds: string } {
    const now = this.getServerTime()

    if (now.isAfter(endTime)) {
      return { hours: '00', minutes: '00', seconds: '00', isEnd: true }
    }

    const targetTime = moment(endTime)

    const duration = moment.duration(targetTime.diff(now))

    const hours = Math.floor(duration.asHours()).toString().padStart(2, '0')
    const minutes = duration.minutes().toString().padStart(2, '0')
    const seconds = duration.seconds().toString().padStart(2, '0')

    return { hours, minutes, seconds, isEnd: false }
  }

  @action
  public recalcState(userInfo: UserInfo) {
    // 刷新列表
    this.page = 0
    this.isLoading = false
    this.list = []
    this.key++
    // 如果主动获取订单列表可能会造成infinite-loading组件状态混乱
    // this.loadOrderList(userInfo)
  }

  public async loadOrderList(userInfo: UserInfo): Promise<EnumWaterfallLoadResult> {
    if (this.isLoading) return EnumWaterfallLoadResult.Cancel

    this.isLoading = true

    const [error, data] = await $service<Array<OrderListItemResponse>>({
      method: 'GET',
      url: `/order/api/orders`,
      params: {
        page: this.page + 1,
        pageSize: this.pageSize,
        lastId: this.list.length > 0 ? this.list[this.list.length - 1].orderNo : '',
        type: this.option
      }
    })

    this.isLoading = false

    if (error) {
      return EnumWaterfallLoadResult.Error
    }

    this.page++

    this.onAppendOrderList(data.map(item => this.parseOrderItem(item, userInfo)))

    return data.length === this.pageSize ? EnumWaterfallLoadResult.Loaded : EnumWaterfallLoadResult.Complete
  }

  public async onCheckRefund(orderNo: string) {
    const [, res] = await $service<CheckRefundResult>({
      method: 'GET',
      url: EnumApiOrder.CheckRefund,
      data: { orderNo },
      params: { orderNo }
    })

    return res
  }

  public onReset() {
    this.page = 0
    this.list = []
    this.option = ''
    this.isLoading = false
  }
}
